/**
 * <fade-in> Web Component
 *
 * Wraps any children and animates them in with a staggered fade + slide-up
 * when they scroll into view. Uses IntersectionObserver for performance.
 *
 * Attributes:
 *   threshold       – 0-1, how much must be visible to trigger (default: 0.2)
 *   stagger         – ms between each child's animation (default: 150)
 *   duration        – ms for each child's transition (default: 600)
 *   distance        – px to slide up from (default: 24)
 *   child-selector  – animate only matching descendants (default: direct children)
 *
 * Usage:
 *   <fade-in stagger="150" distance="24">
 *     <h2>Title</h2>
 *     <p>Text</p>
 *   </fade-in>
 *
 * The animation writes inline transforms, so anything already positioned with
 * one — an absolutely placed overlay, a rotated sticker — must be kept out of
 * it. That is what child-selector is for.
 */
class FadeIn extends HTMLElement {
  connectedCallback() {
    // Respect reduced-motion preferences and the theme editor:
    // leave content in its natural state, no animation.
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || window.Shopify?.designMode) {
      return;
    }

    const threshold = parseFloat(this.getAttribute('threshold') || '0.2');
    const stagger = parseInt(this.getAttribute('stagger') || '150', 10);
    const duration = parseInt(this.getAttribute('duration') || '600', 10);
    const distance = parseInt(this.getAttribute('distance') || '24', 10);

    // Style direct children, or only those matching child-selector
    const childSelector = this.getAttribute('child-selector');
    const children = childSelector
      ? Array.from(this.querySelectorAll(childSelector))
      : Array.from(this.children);
    if (children.length === 0) return;
    children.forEach((child, i) => {
      child.style.opacity = '0';
      child.style.transform = `translateY(${distance}px)`;
      child.style.transition = `opacity ${duration}ms ease, transform ${duration}ms ease`;
      child.style.transitionDelay = `${i * stagger}ms`;
    });

    // Observe
    const once = this.hasAttribute('once');
    let revealed = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealed = true;
            children.forEach((child) => {
              child.style.opacity = '1';
              child.style.transform = 'translateY(0)';
            });
            if (once) observer.disconnect();
          } else if (!(once && revealed)) {
            // Reset so it animates again next time. A one-shot reveal never
            // resets: a single callback can carry the intersecting entry and a
            // later non-intersecting one, and forEach keeps going past
            // disconnect(), which would re-hide the content for good.
            children.forEach((child) => {
              child.style.opacity = '0';
              child.style.transform = `translateY(${distance}px)`;
            });
          }
        });
      },
      { threshold }
    );

    observer.observe(this);
  }
}

if (!customElements.get('fade-in')) {
  customElements.define('fade-in', FadeIn);
}
